import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiService } from "../utils/api";
import "./WriteDocument.css"; // Add this import
import { initSecurityMeasures } from "../utils/security";

function WriteDocument() {
  const [teacherDocuments, setTeacherDocuments] = useState([]);
  const [selectedDocumentId, setSelectedDocumentId] = useState("");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const startTimeRef = useRef(null);
  const [currentDocumnet, setCurrentDocument] = useState({});

  const navigate = useNavigate();

  // Add timer states
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [formattedTime, setFormattedTime] = useState("0:00");
  const timerRef = useRef(null);

  // useEffect(() => {
  //   const checkDevTools = () => {
  //     const start = performance.now();
  //     debugger; // Will pause if devtools are open
  //     const end = performance.now();
  //     if (end - start > 100) {
  //       console.warn("DevTools detected");
  //       // handle detection here
  //     }
  //   };

  //   const interval = setInterval(checkDevTools, 3000);

  //   return () => clearInterval(interval);
  // }, []);

  initSecurityMeasures();

  useEffect(() => {
    const fetchTeacherDocuments = async () => {
      try {
        const response = await apiService.getDocumentByStudentBatch();
        const data = response.data;

        if (data.success) {
          setTeacherDocuments(data.documents);
        } else {
          setError(data.message);
        }
      } catch (err) {
        setError(err.response?.data?.message || "Error fetching teacher documents");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchTeacherDocuments();

    // Cleanup timer on unmount
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Timer functions
  const startTimer = useCallback(() => {
    if (isTimerRunning) return;

    if (elapsedTime === 0) {
      setContent("");
    }

    setIsTimerRunning(true);
    startTimeRef.current = Date.now() - elapsedTime * 1000;

    timerRef.current = setInterval(() => {
      const currentTime = Date.now();
      const currentElapsed = Math.floor((currentTime - startTimeRef.current) / 1000);
      setElapsedTime(currentElapsed);

      const minutes = Math.floor(currentElapsed / 60);
      const seconds = currentElapsed % 60;
      if (Number(currentDocumnet.timeTaken.split(":")[0]) <= minutes) {

        clearInterval(timerRef.current);
        setIsTimerRunning(false);
      }

      setFormattedTime(`${minutes}:${seconds.toString().padStart(2, "0")}`);
    }, 1000);
  }, [currentDocumnet.timeTaken, elapsedTime, isTimerRunning]);

  const stopTimer = useCallback(() => {
    if (!isTimerRunning) return;

    clearInterval(timerRef.current);
    setIsTimerRunning(false);
  }, [isTimerRunning]);

  const resetTimer = () => {
    stopTimer();
    setElapsedTime(0);
    setFormattedTime("0:00");
    setContent("");
  };

  const handleDocumentSelect = async (e) => {
    const docId = e.target.value;
    let docName = teacherDocuments.find((doc) => parseInt(doc.id) === parseInt(docId))?.title;
    setCurrentDocument(teacherDocuments.find((doc) => parseInt(doc.id) === parseInt(docId)));
    setTitle(docName);
    setSelectedDocumentId(docId);

    // Reset timer when selecting a new document
    resetTimer();
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    // Stop the timer when submitting
    stopTimer();

    if (!selectedDocumentId) {
      setError("Please select a document to compare with");
      return;
    }

    if (!content.trim()) {
      setError("Please enter some content");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const response = await apiService.compareDocuments({
        originalDocumentId: selectedDocumentId,
        studentText: content,
        title,
        timeTaken: formattedTime,
      });
      const data = response.data;

      if (data.success) {
        navigate(`/comparison-results/${data.document.id}`, {
          state: {
            comparisonResults: data.comparison,
            documentId: data.document.id,
            timeTaken: data.timeTaken,
            // Pass the time taken to the results page
          },
        });
      } else {
        setError(data.message || "Failed to compare documents");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error connecting to server");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p>Loading documents...</p>;
  }

  return (
    <div className="write-document-container">
      <h2>Write Document</h2>

      <form onSubmit={handleSubmit} className="write-form">
        <div className="form-group">
          <label>Select Teacher Document:</label>
          <select value={selectedDocumentId} onChange={handleDocumentSelect} className="document-select" disabled={isTimerRunning}>
            <option value="">-- Select a document --</option>
            {teacherDocuments.map((doc) => (
              <option key={doc.id} value={doc.id}>
                {doc.title}
              </option>
            ))}
          </select>
        </div>

        {selectedDocumentId && (
          <>
            <div className="form-group">
              {/* <label>Your Document Title:</label> */}
              <input type="hidden" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Enter document title" className="title-input" disabled={isTimerRunning} />
            </div>

            {/* Timer display and controls */}
            <div className="timer-container">
              <div className="timer-display">
                <div className="timer-circle">
                  <span className="timer-value">{formattedTime}</span>
                  <span className="timer-label">Time Elapsed</span>
                </div>
              </div>
              <div className="timer-controls">
                {!isTimerRunning ? (
                  <button type="button" onClick={startTimer} className="start-button" disabled={!selectedDocumentId}>
                    <i className="fas fa-play"></i> Start Typing
                  </button>
                ) : (
                  <button type="button" onClick={stopTimer} className="pause-button">
                    <i className="fas fa-pause"></i> Pause
                  </button>
                )}
                <button type="button" onClick={resetTimer} className="reset-button" disabled={elapsedTime === 0}>
                  <i className="fas fa-redo"></i> Reset
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>Your Version:</label>
              <textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder={isTimerRunning ? "Start typing..." : "Click 'Start Typing' to begin"} className="content-textarea" rows="15" disabled={!isTimerRunning} />
            </div>

            <div className="button-group">
              <button type="button" onClick={() => navigate("/dashboard/student")} className="cancel-button">
                Cancel
              </button>
              <button type="submit" className="save-button" disabled={saving || !selectedDocumentId || !content.trim()}>
                {saving ? "Comparing..." : "Compare & Save"}
              </button>
            </div>
          </>
        )}

        {error && <p className="error-message">{error}</p>}
      </form>
    </div>
  );
}

export default WriteDocument;
